import { put, select } from 'redux-saga/effects';
import cloneDeep from 'lodash.clonedeep';
import { Rectangle } from 'pixi.js';

import { allCamerasPinned } from 'js/shared/allCamerasPinned';
import getNewCamera from 'js/shared/helpers/getNewCamera';
import { getEditorCanvas } from 'js/editor/EditorCanvas/EditorCanvas';
import {
  getAdjustedCameraRectForBounds,
  getAdjustedNewCameraRect
} from 'js/editor/EditorCanvas/helpers/getNewCameraRect';
import { ExistingScribeModel } from 'js/types';
import { AddCameraAction } from 'js/actionCreators/cameraActions';
import { updateScribe } from 'js/actionCreators/scribeActions';

import { getActiveElements, getScribeById } from '../selectors';

export function* addCamera({ projectId, fitTo }: AddCameraAction) {
  const project: ExistingScribeModel | undefined = yield select(getScribeById, projectId);
  if (!project) return;

  const clonedProject = cloneDeep(project);
  const activeScene = clonedProject.scenes.find(scene => scene.active);
  if (!activeScene) return;
  const areAllPinned = allCamerasPinned(clonedProject);
  const selectedElementIds: Array<string> = yield select(getActiveElements);

  const cameraProps = getNewCamera();
  cameraProps.cameraPinned = areAllPinned;
  const newCameraProps = {
    ...cameraProps,
    animationTime: 1
  };

  const editorCanvas = getEditorCanvas();

  if (editorCanvas) {
    const viewportRectAndCenter = editorCanvas.getViewportRectAndCenter();

    if (viewportRectAndCenter && fitTo === 'view') {
      const newCameraBounds = getAdjustedNewCameraRect(
        viewportRectAndCenter,
        editorCanvas.canvasSize,
        editorCanvas.canvasBounds
      );

      newCameraProps.scale = newCameraBounds.scale;
      newCameraProps.x = newCameraBounds.adjustedCameraRectangle.x;
      newCameraProps.y = newCameraBounds.adjustedCameraRectangle.y;
    } else if (fitTo === 'elements') {
      const activeSelectionBounds = editorCanvas.getActiveSelectionBoundingBox();
      if (activeSelectionBounds) {
        const newCameraBounds = getAdjustedCameraRectForBounds({
          bounds: new Rectangle(
            activeSelectionBounds.left,
            activeSelectionBounds.top,
            activeSelectionBounds.width,
            activeSelectionBounds.height
          ),
          canvasSize: editorCanvas.canvasSize
        });
        newCameraProps.scale = newCameraBounds.scale;
        newCameraProps.x = newCameraBounds.adjustedCameraRectangle.x;
        newCameraProps.y = newCameraBounds.adjustedCameraRectangle.y;
      }
    }
  }

  let lowestValidIndex = selectedElementIds.reduce((lowest, id) => {
    const elementIndex = activeScene.elementIds.findIndex(elId => elId === id);
    if (elementIndex === -1) return lowest;

    return Math.min(lowest, elementIndex);
  }, activeScene.elementIds.length - 1);

  if (fitTo === 'view') {
    if (selectedElementIds.length === 1 && lowestValidIndex > 0) {
      const selectedElement = project.elements.find(el => el.id === activeScene.elementIds[lowestValidIndex]);
      if (selectedElement && selectedElement.type === 'Camera') lowestValidIndex++;
      activeScene.elementIds.splice(lowestValidIndex, 0, newCameraProps.id);
    } else if (selectedElementIds.length > 1) {
      const highestValidIndex = selectedElementIds.reduce((highest, id) => {
        const elementIndex = activeScene.elementIds.findIndex(elId => elId === id);
        if (elementIndex === -1) return highest;

        return Math.max(highest, elementIndex);
      }, 0);
      activeScene.elementIds.splice(highestValidIndex + 1, 0, newCameraProps.id);
    } else if (lowestValidIndex === 0) {
      // If the start camera is selected then we place the new camera after the start camera
      activeScene.elementIds.splice(1, 0, newCameraProps.id);
    } else {
      activeScene.elementIds.push(newCameraProps.id);
    }
  } else if (fitTo === 'elements') {
    if (lowestValidIndex > 0) {
      // Only one element is selected so place camera before that element
      const selectedElement = project.elements.find(el => el.id === activeScene.elementIds[lowestValidIndex]);
      if (selectedElement && selectedElement.type === 'Camera') lowestValidIndex++;
      activeScene.elementIds.splice(lowestValidIndex, 0, newCameraProps.id);
    } else if (lowestValidIndex === 0) {
      // If the start camera is selected then we place the new camera after the start camera
      activeScene.elementIds.splice(1, 0, newCameraProps.id);
    }
  }

  clonedProject.elements.push(newCameraProps);

  yield put(updateScribe(clonedProject, false, [newCameraProps.id]));
}
